import { Link, Outlet, useLocation, useParams } from "react-router";
import { useTranslation } from "react-i18next";
import { useAuth, useCan } from "../../auth/AuthProvider";
import { DetailHeader } from "../../components/DetailHeader";
import { Alert, Button, LoadingBlock, Mono, StateBadge, buttonStyles } from "../../components/ui";
import { describePlan } from "../../components/PlanPicker";
import { describeSource } from "../../lib/format";
import { useApp, useAppLifecycle, useDomains, type LifecycleAction } from "../../lib/queries";
import { regionLabel } from "../../lib/regions";
import { vanishedWhileCreating } from "../../lib/vanished";

/* Route and key per tab; the words are in `app.json` under `tabs`. */
const TABS = [
  { to: ".", key: "overview", end: true },
  /* Releases and runtime logs, with a sub-nav of their own inside. */
  { to: "deployments", key: "deployments" },
  { to: "env", key: "env" },
  { to: "domains", key: "domains" },
  { to: "schedules", key: "schedules" },
  { to: "settings", key: "settings" },
] as const;

export function AppDetailPage() {
  const { t } = useTranslation("app");
  const { appId } = useParams();
  const { currentOrg } = useAuth();
  const canDeploy = useCan("developer");
  const { data: app, isPending, error } = useApp(currentOrg?.id, appId);
  const lifecycle = useAppLifecycle(currentOrg?.id ?? "", appId ?? "");
  /*
   * Fetched by the header rather than only by the Domains tab, because the
   * live address belongs at the top of every tab. It is cheap: the same query
   * key the tab uses, so opening that tab is served from cache.
   */
  const { data: domains } = useDomains(currentOrg?.id, appId);

  const location = useLocation();
  const creationWarning =
    typeof (location.state as { warning?: unknown } | null)?.warning === "string"
      ? (location.state as { warning: string }).warning
      : null;

  if (isPending) return <LoadingBlock />;
  if (error) {
    // A draft the engine never confirmed can vanish under the page: the API
    // removes a create that never finished. Say so, rather than "not found".
    const vanished = vanishedWhileCreating(error, "app", app?.status);
    return (
      <Alert>
        {vanished ?? error.message}
        {vanished && (
          <>
            {" "}
            <Link to="/apps" className="underline">
              {t("backLink")}
            </Link>
          </>
        )}
      </Alert>
    );
  }
  if (!app) return <Alert>{t("notFound")}</Alert>;

  const state = app.status.value;
  const primary = domains?.[0];

  /*
   * A Dockerfile or static build reuses the layers the last one cached, and now
   * and then one of them is stale. Railpack starts every build without a cache
   * already, and an image has nothing to build, so neither is offered this.
   */
  const cleanable =
    (app.buildType === "dockerfile" || app.buildType === "static") && app.source.type !== "docker";

  /* A clean rebuild is a redeploy too, so the spinner also goes by the flag. */
  const busy = (action: LifecycleAction, cleanCache = false) =>
    lifecycle.isPending &&
    lifecycle.variables?.action === action &&
    Boolean(lifecycle.variables.cleanCache) === cleanCache;

  /* Ghost, and first in the row: a slower build for a rare fault, so it sits
     furthest from the primary and has no chrome until it is pointed at. Last on
     a phone, where the row wraps and would otherwise strand the primary below it. */
  const rebuildClean = cleanable && (
    <Button
      variant="ghost"
      className="order-last sm:order-none"
      title={t("actions.rebuildWithoutCacheHint")}
      onClick={() =>
        lifecycle.mutate({ action: "redeploy", title: t("actions.cleanRebuild"), cleanCache: true })
      }
      loading={busy("redeploy", true)}
    >
      {t("actions.rebuildWithoutCache")}
    </Button>
  );

  /*
   * Lifecycle controls follow the resolved state, not the stored column. A
   * release in flight is a review task, a draft needs its first deploy, and a
   * failed app needs the failed record before someone blindly tries again.
   */
  const actions = !canDeploy ? undefined : state === "draft" ? (
    <Button
      icon="rocket"
      onClick={() => lifecycle.mutate({ action: "deploy", title: t("actions.firstDeployment") })}
      loading={lifecycle.isPending}
    >
      {t("actions.deploy")}
    </Button>
  ) : app.status.stale ? (
    <Link to="deployments" relative="path" className={buttonStyles("secondary")}>
      {t("actions.review")}
    </Link>
  ) : state === "deploying" ? (
    <Link to="deployments" relative="path" className={buttonStyles("secondary")}>
      {t("actions.viewDeployment")}
    </Link>
  ) : state === "running" ? (
    <>
      {rebuildClean}
      <Button
        variant="secondary"
        onClick={() => lifecycle.mutate({ action: "stop" })}
        loading={busy("stop")}
      >
        {t("actions.stop")}
      </Button>
      <Button
        icon="rocket"
        onClick={() => lifecycle.mutate({ action: "redeploy", title: t("actions.manualRedeploy") })}
        loading={busy("redeploy")}
      >
        {t("actions.redeploy")}
      </Button>
    </>
  ) : state === "stopped" ? (
    <>
      {rebuildClean}
      <Button
        variant="secondary"
        onClick={() => lifecycle.mutate({ action: "redeploy", title: t("actions.manualRedeploy") })}
        loading={busy("redeploy")}
      >
        {t("actions.rebuild")}
      </Button>
      <Button
        icon="rocket"
        onClick={() => lifecycle.mutate({ action: "start" })}
        loading={busy("start")}
      >
        {t("actions.start")}
      </Button>
    </>
  ) : state === "error" ? (
    <>
      {rebuildClean}
      <Link to="deployments" relative="path" className={buttonStyles("secondary")}>
        {t("actions.viewFailed")}
      </Link>
      <Button
        icon="rocket"
        onClick={() => lifecycle.mutate({ action: "redeploy", title: t("actions.retry") })}
        loading={busy("redeploy")}
      >
        {t("actions.redeploy")}
      </Button>
    </>
  ) : null;

  /*
   * Under the buttons rather than in an alert: a fact about the next build, not
   * news, and it goes by itself once that build has run. Shown to a viewer too,
   * who has no buttons here but may well be wondering why a build is slow.
   */
  const headerActions = app.cleanCachePending ? (
    <div className="flex flex-col gap-2 sm:items-end">
      {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
      <p className="text-[13px] text-fg-subtle sm:text-right">{t("actions.cleanCachePending")}</p>
    </div>
  ) : (
    actions
  );

  return (
    <div className="space-y-6">
      <DetailHeader
        backTo="/apps"
        backLabel={t("back")}
        name={app.name}
        badge={<StateBadge state={app.status.value} stale={app.status.stale} />}
        address={
          app.defaultDomain
            ? {
                value: app.defaultDomain,
                href: `https://${app.defaultDomain}`,
                copyLabel: t("copyAddress"),
              }
            : primary
              ? { value: primary.url, href: primary.url, copyLabel: t("copyAddress") }
              : {
                  value: "",
                  copyLabel: "",
                  empty: (
                    <span className="text-sm text-fg-subtle">{t("noDomain")}</span>
                  ),
                }
        }
        actions={headerActions}
        meta={[
          { label: t("meta.source"), value: <Mono>{describeSource(app.source)}</Mono> },
          { label: t("meta.port"), value: app.port ?? "—" },
          {
            label: t("meta.plan"),
            value: app.plan ? describePlan(app.plan) : t("meta.noPlan"),
          },
          // Where it runs. A fact with no control: an app cannot move.
          { label: t("meta.region"), value: regionLabel(app.region) ?? "—" },
        ]}
        tabs={TABS.map((tab) => ({ ...tab, label: t(`tabs.${tab.key}`) }))}
      />

      {lifecycle.error && <Alert>{lifecycle.error.message}</Alert>}
      {lifecycle.data?.rebuilding && state === "deploying" && (
        <Alert tone="info">{t("actions.rebuildingOnStart")}</Alert>
      )}

      {/* Carried here by the create form. The app was created, but something
          alongside it was not — most often the push webhook — and this is the
          moment the customer is looking, so it is the moment to say so. */}
      {creationWarning && <Alert tone="warning">{creationWarning}</Alert>}

      <Outlet context={{ orgId: currentOrg?.id, appId }} />
    </div>
  );
}
